/**
 * SequencingGroupDetailPage — iter150AF (Phase 1B).
 *
 * Tabs:
 *   • Members   — list + add + remove devices, set control-mode / FAD per device
 *   • Schedule  — per-weekday start/end + run flag
 *   • Tests     — start + complete fill / loss tests, with history list
 *   • Decisions — read-only log of engine decisions (empty until Phase 1C)
 *
 * Tenant scope is enforced server-side; UI just renders what the API returns.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Stack, ArrowLeft, ArrowRight, Plus, Trash, Spinner, X, GearSix, CalendarBlank,
  Flask, Flask as FlaskIcon, ChartLineUp, Drop, Drop as DropletIcon, Gauge,
  CheckCircle, Warning, Info,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  seqGetGroup, seqPatchGroup, seqAddMember, seqRemoveMember,
  seqMemberStartStop, seqGroupDashboard,
  seqGetControlMode, seqSetControlMode, seqListFad, seqUpsertFad, seqDeleteFad,
  seqListSchedule, seqUpsertScheduleSlot, seqDeleteScheduleSlot,
  seqReplaceSchedule, seqCapacityAnalysis, seqCapacityAnalysisPdfUrl,
  seqLeakAlertStatus, seqLeakAlertSend,
  seqListFillTests, seqStartFillTest, seqCompleteFillTest,
  seqListLossTests, seqStartLossTest, seqCompleteLossTest,
  seqListDecisions,
  seqGroupEngine, seqGroupEngineEnable, seqGroupEngineDisable,
  seqGroupEngineTickNow, seqEngineWorkerStatus,
} from "@/lib/sequencingApi";
import { useT } from "@/lib/i18n";
import PolicyRecommenderCard from "@/components/sequencing/PolicyRecommenderCard";
import LearnMoreChip from "@/components/help/LearnMoreChip";

const TABS = [
  { key: "members",   label: "Members + FAD", icon: Gauge },
  { key: "schedule",  label: "Schedule",      icon: CalendarBlank },
  { key: "tests",     label: "Tests",         icon: Flask },
  { key: "settings",  label: "Settings",      icon: GearSix },
  { key: "decisions", label: "Engine",        icon: ChartLineUp },
];

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];


export default function SequencingGroupDetailPage() {
  const { gid } = useParams();
  const t = useT();
  const [group, setGroup]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState("");
  const [tab, setTab]         = useState("members");

  async function refresh() {
    setLoading(true); setErr("");
    try {
      const g = await seqGetGroup(gid);
      setGroup(g);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, [gid]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="seq-group-detail">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to="/sequencing/groups" data-testid="seq-group-back"
              className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <Stack size={20} weight="fill" className="text-blue-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
            {group?.name || "—"}
          </h1>
          <p className="text-[11px] text-slate-500 truncate">
            {group?.customer_name
              ? <span className="font-bold text-slate-700">{group.customer_name}</span>
              : <span className="italic text-slate-400">unlinked tenant</span>}
            {group?.common_pset != null && <> · Pset {group.common_pset} bar</>}
            {group?.bandwidth_pct != null && <> · ±{group.bandwidth_pct}%</>}
          </p>
        </div>
        {group?.state && (
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border
                            ${group.state === "auto_sequencing"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                              : "bg-slate-100 text-slate-600 border-slate-300"}`}
                data-testid={`seq-group-state-${group.state}`}>
            {group.state}
          </span>
        )}
        <Link to={`/sequencing/groups/${gid}/live`}
              data-testid="seq-group-live-link"
              className="ml-1 text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
          Live
        </Link>
        <Link to={`/sequencing/groups/${gid}/commissioning`}
              data-testid="seq-group-commissioning-link"
              className="ml-1 text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-blue-600 bg-blue-600 text-white hover:bg-blue-700">
          Commissioning <ArrowRight size={11} weight="bold" />
        </Link>
      </header>

      <nav className="bg-white border-b border-slate-200 px-4 md:px-6 flex gap-1 overflow-x-auto">
        {TABS.map(tt => {
          const Icon = tt.icon;
          const active = tab === tt.key;
          return (
            <button
              key={tt.key}
              type="button"
              onClick={() => setTab(tt.key)}
              data-testid={`seq-group-tab-${tt.key}`}
              className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 border-b-2
                          ${active ? "border-blue-600 text-blue-700"
                                   : "border-transparent text-slate-500 hover:text-slate-800"}`}
            >
              <Icon size={12} weight="bold" /> {tt.label}
            </button>
          );
        })}
      </nav>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6">
        {err && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm">{err}</div>}
        {loading || !group ? (
          <div className="py-10 text-center"><Spinner size={20} className="mx-auto animate-spin text-slate-400" /></div>
        ) : (
          <>
            {tab === "members"   && <MembersTab    group={group} onChanged={refresh} />}
            {tab === "schedule"  && <ScheduleTab   gid={gid} />}
            {tab === "tests"     && <TestsTab      gid={gid} members={group.members || []} />}
            {tab === "settings"  && <SettingsTab   group={group} onChanged={refresh} />}
            {tab === "decisions" && <DecisionsTab  gid={gid} />}
          </>
        )}
      </main>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Tab 1 — Members + per-device control-mode + FAD@Pset
// ════════════════════════════════════════════════════════════════════
function MembersTab({ group, onChanged }) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const [newDevice, setNewDevice] = useState("");
  const [busy, setBusy] = useState(false);
  // iter150AG Part 18 — pull live running_status per member from the
  // dashboard snapshot so the Start/Stop toggle colour reflects reality
  // instead of stale state. Refreshes every 5s.
  const [runningByDid, setRunningByDid] = useState({});
  useEffect(() => {
    let alive = true;
    const tick = () => {
      seqGroupDashboard(group.id)
        .then(d => {
          if (!alive) return;
          const map = {};
          (d.members || []).forEach(m => { map[m.device_id] = m.running_status; });
          setRunningByDid(map);
        })
        .catch(() => { /* tolerate transient failures */ });
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [group.id]);

  async function addDevice(e) {
    e?.preventDefault?.();
    const id = newDevice.trim();
    if (!id) return;
    setBusy(true);
    try {
      await seqAddMember(group.id, { device_id: id });
      setNewDevice("");
      setAdding(false);
      toast.success(t("seq.member_added", "Device added"));
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message);
    } finally { setBusy(false); }
  }

  async function removeDevice(deviceId) {
    if (!window.confirm(`Remove device ${deviceId} from this group?`)) return;
    try {
      await seqRemoveMember(group.id, deviceId);
      toast.success(t("seq.member_removed", "Device removed"));
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message);
    }
  }

  return (
    <div className="space-y-3" data-testid="seq-tab-members">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600">
          {t("seq.members_title", "Members")} ({(group.members || []).length})
        </h2>
        {adding ? (
          <form onSubmit={addDevice} className="flex items-center gap-1">
            <input value={newDevice} onChange={e => setNewDevice(e.target.value)} autoFocus
                   placeholder="Serial number"
                   data-testid="seq-members-new-device"
                   className="px-2 py-1.5 text-xs border border-slate-300 rounded-sm font-mono focus:border-blue-500 focus:outline-none" />
            <button type="submit" disabled={busy || !newDevice.trim()}
                    data-testid="seq-members-add-submit"
                    className="text-[10px] font-bold uppercase px-2 py-1.5 rounded-sm bg-blue-700 text-white disabled:opacity-50">
              {busy ? <Spinner size={10} className="animate-spin" /> : <Plus size={10} weight="bold" />}
            </button>
            <button type="button" onClick={() => { setAdding(false); setNewDevice(""); }}
                    data-testid="seq-members-add-cancel"
                    className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100">
              <X size={12} weight="bold" />
            </button>
          </form>
        ) : (
          <button type="button" onClick={() => setAdding(true)} data-testid="seq-members-add-open"
                  className="text-[10px] font-bold uppercase px-2 py-1.5 rounded-sm bg-blue-700 text-white inline-flex items-center gap-1">
            <Plus size={10} weight="bold" /> {t("seq.add_device", "Add device")}
          </button>
        )}
      </div>

      {/* iter150AG Part 18 — Column-header explainer banner. */}
      <div data-testid="seq-members-legend"
           className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 grid grid-cols-[1fr_1fr_1fr_1fr] gap-3 text-[10px] leading-snug">
        <div>
          <div className="font-bold uppercase tracking-wider text-slate-700">Serial</div>
          <div className="text-slate-500">The compressor's industrial serial number.</div>
        </div>
        <div>
          <div className="font-bold uppercase tracking-wider text-slate-700">Control mode</div>
          <div className="text-slate-500">
            <b>local</b> = on-panel only · <b>remote</b> = SCADA only ·
            <b> auto</b> = sequencer can issue start/stop commands.
          </div>
        </div>
        <div>
          <div className="font-bold uppercase tracking-wider text-slate-700">FAD curve</div>
          <div className="text-slate-500">
            Free-Air Delivery rating (m³/min) at each Pset (bar). Drives
            the engine's capacity-matching maths. Count is in parentheses.
          </div>
        </div>
        <div>
          <div className="font-bold uppercase tracking-wider text-slate-700">Run state</div>
          <div className="text-slate-500">
            One-click manual start/stop. Green = running, red = stopped.
            Overrides the engine; use sparingly.
          </div>
        </div>
      </div>

      {(group.members || []).length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-sm p-6 text-center text-slate-500 text-sm">
          {t("seq.members_empty", "No devices in this group yet")}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="seq-members-list">
          {group.members.map(m => (
            <MemberRow key={m.device_id} member={m}
                       groupId={group.id}
                       running={runningByDid[m.device_id]}
                       onRemove={() => removeDevice(m.device_id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function MemberRow({ member, groupId, running, onRemove }) {
  const t = useT();
  const [mode, setMode] = useState(null);
  const [fad, setFad]   = useState([]);
  const [showFad, setShowFad] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    let alive = true;
    seqGetControlMode(member.device_id)
      .then(d => { if (alive) setMode(d.mode); })
      .catch(() => { if (alive) setMode(null); });
    seqListFad(member.device_id)
      .then(d => { if (alive) setFad(d.items || []); })
      .catch(() => { if (alive) setFad([]); });
    return () => { alive = false; };
  }, [member.device_id]);

  async function changeMode(m) {
    try {
      await seqSetControlMode(member.device_id, m);
      setMode(m);
      toast.success(`mode → ${m}`);
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message); }
  }

  // iter150AG Part 18 — manual start / stop toggle.
  // ``running`` is true | false | undefined (no telemetry yet).
  async function toggleRunning() {
    if (toggling) return;
    const action = running ? "stop" : "start";
    const confirmMsg = action === "stop"
      ? `Stop ${displayName} now? This bypasses the sequencing engine.`
      : `Start ${displayName} now? This bypasses the sequencing engine.`;
    if (!window.confirm(confirmMsg)) return;
    setToggling(true);
    try {
      await seqMemberStartStop(groupId, member.device_id, action);
      toast.success(`${action} sent to ${displayName}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message);
    } finally { setToggling(false); }
  }

  const displayName = member.serial_number || member.device_id;
  const isRunning = running === true || running === 1 || running === 1.0;
  const isStopped = running === false || running === 0 || running === 0.0;

  return (
    <li className="bg-white border border-slate-200 rounded-sm p-3" data-testid={`seq-member-${member.device_id}`}>
      <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-center">
        {/* 1. Serial */}
        <div className="font-mono text-sm font-bold text-slate-900 truncate"
             data-testid={`seq-member-serial-${member.device_id}`}
             title={member.device_id}>
          {displayName}
        </div>
        {/* 2. Mode */}
        <select value={mode || ""}
                onChange={e => changeMode(e.target.value)}
                data-testid={`seq-member-mode-${member.device_id}`}
                className="text-[11px] px-2 py-1 border border-slate-300 rounded-sm bg-white w-full">
          <option value="">{t("seq.mode_unset", "(unset)")}</option>
          <option value="local">local</option>
          <option value="remote">remote</option>
          <option value="auto">auto</option>
        </select>
        {/* 3. FAD toggle */}
        <button type="button" onClick={() => setShowFad(!showFad)}
                data-testid={`seq-member-fad-toggle-${member.device_id}`}
                className="text-[10px] font-bold uppercase px-2 py-1 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1 justify-self-start">
          <DropletIcon size={10} weight="fill" />
          FAD ({fad.length})
        </button>
        {/* 4. Run state toggle */}
        <button type="button" onClick={toggleRunning} disabled={toggling}
                data-testid={`seq-member-runstate-${member.device_id}`}
                title={isRunning
                  ? "Compressor is running — click to STOP"
                  : isStopped
                    ? "Compressor is stopped — click to START"
                    : "Run-state unknown — click anyway to send a START"}
                className={`text-[10px] font-bold uppercase px-2 py-1.5 rounded-sm
                            inline-flex items-center gap-1.5 justify-self-start
                            transition-colors disabled:opacity-50
                            ${isRunning
                              ? "bg-emerald-600 text-white hover:bg-emerald-700"
                              : isStopped
                                ? "bg-rose-600 text-white hover:bg-rose-700"
                                : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}>
          {toggling ? (
            <Spinner size={10} className="animate-spin" />
          ) : (
            <span className={`inline-block w-2 h-2 rounded-full
                              ${isRunning ? "bg-emerald-200"
                                : isStopped ? "bg-rose-200" : "bg-slate-400"}`} />
          )}
          {isRunning ? "Running" : isStopped ? "Stopped" : "Unknown"}
          <span className="opacity-75 ml-0.5">
            ({isRunning ? "stop" : "start"})
          </span>
        </button>
        {/* 5. Remove */}
        <button type="button" onClick={onRemove}
                data-testid={`seq-member-remove-${member.device_id}`}
                className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-red-700 hover:bg-red-50">
          <Trash size={12} weight="bold" />
        </button>
      </div>
      {showFad && (
        <FadEditor deviceId={member.device_id} rows={fad}
                   onChange={(rows) => setFad(rows)} />
      )}
    </li>
  );
}

function FadEditor({ deviceId, rows, onChange }) {
  const [pset, setPset] = useState("");
  const [val,  setVal]  = useState("");
  const [busy, setBusy] = useState(false);

  async function add(e) {
    e?.preventDefault?.();
    if (!pset || !val) return;
    setBusy(true);
    try {
      await seqUpsertFad(deviceId, Number(pset), Number(val));
      const fresh = await seqListFad(deviceId);
      onChange(fresh.items || []);
      setPset(""); setVal("");
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message); }
    finally { setBusy(false); }
  }

  async function del(p) {
    try {
      await seqDeleteFad(deviceId, p);
      const fresh = await seqListFad(deviceId);
      onChange(fresh.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message); }
  }

  return (
    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2" data-testid={`seq-fad-editor-${deviceId}`}>
      <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 pb-1">
        <span>Pset (bar)</span>
        <span>FAD (m³/min)</span>
        <span></span>
        <span></span>
      </div>
      {rows.map(r => (
        <div key={r.pset_bar} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1 items-center"
             data-testid={`seq-fad-row-${deviceId}-${r.pset_bar}`}>
          <span className="text-sm font-mono">{r.pset_bar}</span>
          <span className="text-sm font-mono">{r.fad_m3_per_min}</span>
          <span></span>
          <button type="button" onClick={() => del(r.pset_bar)}
                  data-testid={`seq-fad-delete-${deviceId}-${r.pset_bar}`}
                  className="w-6 h-6 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-red-700 hover:bg-red-50">
            <Trash size={10} weight="bold" />
          </button>
        </div>
      ))}
      <form onSubmit={add} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1">
        <input value={pset} onChange={e => setPset(e.target.value)} type="number" step="0.1"
               placeholder="8.0"
               data-testid={`seq-fad-new-pset-${deviceId}`}
               className="px-2 py-1 text-sm border border-slate-300 rounded-sm font-mono focus:border-blue-500 focus:outline-none" />
        <input value={val} onChange={e => setVal(e.target.value)} type="number" step="0.01"
               placeholder="5.0"
               data-testid={`seq-fad-new-val-${deviceId}`}
               className="px-2 py-1 text-sm border border-slate-300 rounded-sm font-mono focus:border-blue-500 focus:outline-none" />
        <button type="submit" disabled={busy || !pset || !val}
                data-testid={`seq-fad-add-${deviceId}`}
                className="text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-blue-700 text-white disabled:opacity-50">
          {busy ? <Spinner size={10} className="animate-spin" /> : <Plus size={10} weight="bold" />}
        </button>
        <span></span>
      </form>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Tab 2 — Schedule (iter150AH: multi-window STOP semantics per weekday).
//
// Each weekday can have ZERO or MORE stop windows. The engine sheds
// all members while NOW falls inside any window. Outside every window
// the group runs as usual. Default (no windows defined): runs 24/7.
//
// Save flow: edits buffer locally; user clicks "Save schedule" to push
// the entire week atomically via the /schedule/bulk endpoint. This
// avoids the old single-slot-per-weekday limitation.
// ════════════════════════════════════════════════════════════════════
function ScheduleTab({ gid }) {
  const t = useT();
  const [byDay, setByDay] = useState(() => emptyWeek());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  function emptyWeek() {
    return { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
  }

  async function refresh() {
    setLoading(true);
    try {
      const d = await seqListSchedule(gid);
      const w = emptyWeek();
      for (const s of (d.items || [])) {
        const wd = Number(s.weekday);
        if (wd < 0 || wd > 6) continue;
        // Legacy allow-list rows (run=true) surface as read-only chips.
        w[wd].push({
          start_time: s.start_time || "",
          end_time:   s.end_time   || "",
          legacy:     s.run === true,
        });
      }
      setByDay(w);
      setDirty(false);
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, [gid]);

  function addWindow(weekday) {
    setByDay(prev => ({
      ...prev,
      [weekday]: [...(prev[weekday] || []),
                  { start_time: "12:00", end_time: "13:00", legacy: false }],
    }));
    setDirty(true);
  }

  function patchWindow(weekday, idx, field, value) {
    setByDay(prev => {
      const next = [...(prev[weekday] || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, [weekday]: next };
    });
    setDirty(true);
  }

  function removeWindow(weekday, idx) {
    setByDay(prev => {
      const next = [...(prev[weekday] || [])];
      next.splice(idx, 1);
      return { ...prev, [weekday]: next };
    });
    setDirty(true);
  }

  async function save() {
    if (saving) return;
    // Flatten to slot list; legacy rows are skipped (they're already
    // in Mongo with run=true and the bulk endpoint wipes-and-replaces,
    // so we re-add them as STOP windows only if user kept them).
    const slots = [];
    for (let wd = 0; wd <= 6; wd++) {
      for (const w of (byDay[wd] || [])) {
        if (!w.start_time || !w.end_time) {
          toast.error(`Day ${WEEKDAY_LABELS[wd]}: start & end time required`);
          return;
        }
        if (w.start_time >= w.end_time) {
          toast.error(`Day ${WEEKDAY_LABELS[wd]}: start ${w.start_time} must be earlier than ${w.end_time}`);
          return;
        }
        slots.push({ weekday: wd, start_time: w.start_time, end_time: w.end_time });
      }
    }
    setSaving(true);
    try {
      await seqReplaceSchedule(gid, slots);
      toast.success(`Schedule saved (${slots.length} stop window${slots.length === 1 ? "" : "s"})`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Save failed");
    } finally { setSaving(false); }
  }

  const totalWindows = Object.values(byDay).reduce((a, list) => a + list.length, 0);

  return (
    <div className="space-y-2" data-testid="seq-tab-schedule">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
          {t("seq.schedule_title", "Weekly stop schedule")}
          <LearnMoreChip slug="04-schedules-stop-windows" />
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600"
                data-testid="seq-schedule-total-count">{totalWindows}</span>
        </h2>
        <button type="button" onClick={save} disabled={!dirty || saving}
                data-testid="seq-schedule-save"
                className="text-[10px] font-bold uppercase px-3 py-1.5 rounded-sm bg-emerald-700 text-white disabled:bg-slate-200 disabled:text-slate-400 inline-flex items-center gap-1">
          {saving ? <Spinner size={10} className="animate-spin" /> : <CheckCircle size={10} weight="bold" />}
          {saving ? "Saving…" : (dirty ? "Save schedule" : "Saved")}
        </button>
      </div>
      <div data-testid="seq-schedule-banner"
           className="bg-blue-50 border border-blue-200 rounded-sm p-2.5 text-[11px] text-blue-900 leading-snug mb-2">
        <b>Default behavior: compressors run 24/7.</b> Add one or more <b>stop windows</b> per weekday — periods when the engine must <b>shed all members</b> (e.g. lunch break, night-shift quiet hours, weekend cleanup). Outside every defined window the group runs as usual. Click <b>Save schedule</b> after editing.
      </div>
      {loading ? (
        <Spinner size={16} className="mx-auto animate-spin text-slate-400" />
      ) : (
        <ul className="space-y-1.5">
          {WEEKDAY_LABELS.map((lbl, wd) => {
            const windows = byDay[wd] || [];
            return (
              <li key={wd}
                  className="bg-white border border-slate-200 rounded-sm p-2.5"
                  data-testid={`seq-schedule-row-${wd}`}>
                <div className="flex items-center gap-3 mb-1.5">
                  <div className="w-12 text-xs font-bold uppercase tracking-wider text-slate-700">{lbl}</div>
                  <span className="text-[10px] text-slate-500 font-mono"
                        data-testid={`seq-schedule-day-count-${wd}`}>
                    {windows.length === 0 ? "no stop windows (runs all day)"
                                          : `${windows.length} stop window${windows.length === 1 ? "" : "s"}`}
                  </span>
                  <button type="button" onClick={() => addWindow(wd)}
                          data-testid={`seq-schedule-add-${wd}`}
                          className="ml-auto text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-blue-700 text-white inline-flex items-center gap-1 hover:bg-blue-800">
                    <Plus size={10} weight="bold" /> Add window
                  </button>
                </div>
                {windows.length > 0 && (
                  <ul className="space-y-1 pl-15">
                    {windows.map((w, idx) => (
                      <li key={idx}
                          className={`flex items-center gap-2 flex-wrap text-xs pl-12 ${w.legacy ? "opacity-70" : ""}`}
                          data-testid={`seq-schedule-window-${wd}-${idx}`}>
                        <span className="text-slate-400">stop</span>
                        <input type="time" value={w.start_time || ""}
                               disabled={w.legacy}
                               onChange={e => patchWindow(wd, idx, "start_time", e.target.value)}
                               data-testid={`seq-schedule-window-start-${wd}-${idx}`}
                               className="text-xs px-2 py-1 border border-slate-300 rounded-sm font-mono disabled:opacity-40" />
                        <span className="text-slate-400">until</span>
                        <input type="time" value={w.end_time || ""}
                               disabled={w.legacy}
                               onChange={e => patchWindow(wd, idx, "end_time", e.target.value)}
                               data-testid={`seq-schedule-window-end-${wd}-${idx}`}
                               className="text-xs px-2 py-1 border border-slate-300 rounded-sm font-mono disabled:opacity-40" />
                        {w.legacy && (
                          <span data-testid={`seq-schedule-legacy-${wd}-${idx}`}
                                title="Legacy allow-list slot — delete to convert this day to STOP semantics."
                                className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-300">
                            legacy
                          </span>
                        )}
                        <button type="button" onClick={() => removeWindow(wd, idx)}
                                data-testid={`seq-schedule-window-remove-${wd}-${idx}`}
                                className="ml-auto w-7 h-7 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-red-700 hover:bg-red-50">
                          <Trash size={12} weight="bold" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Tab 3 — Fill / Loss Tests
// ════════════════════════════════════════════════════════════════════
function TestsTab({ gid, members }) {
  const t = useT();
  const [fill, setFill] = useState([]);
  const [loss, setLoss] = useState([]);

  async function refresh() {
    try {
      const [f, l] = await Promise.all([seqListFillTests(gid), seqListLossTests(gid)]);
      setFill(f.items || []);
      setLoss(l.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message); }
  }
  useEffect(() => { refresh(); }, [gid]);

  async function startFill() {
    // Build a serial → device_id lookup so operators see/pick serials.
    const labels = members
      .map(m => `${m.serial_number || m.device_id}`)
      .join(", ");
    const input = window.prompt(
      `Serial number for fill-test?\nAvailable: ${labels}`,
      members[0]?.serial_number || members[0]?.device_id || "");
    if (!input) return;
    const matched = members.find(m =>
      m.serial_number === input.trim() || m.device_id === input.trim());
    const dev = matched?.device_id || input.trim();
    try { await seqStartFillTest(gid, { device_id: dev }); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || e?.message); }
  }

  async function endFill(tid) {
    const ep = Number(window.prompt("end_pressure (bar)?", "8.0"));
    if (Number.isNaN(ep)) return;
    try { await seqCompleteFillTest(tid, { end_pressure: ep, result: "passed" }); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || e?.message); }
  }

  async function startLoss() {
    try { await seqStartLossTest(gid, {}); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || e?.message); }
  }

  async function endLoss(tid) {
    const ep = Number(window.prompt("end_pressure (bar)?", "7.0"));
    if (Number.isNaN(ep)) return;
    try { await seqCompleteLossTest(tid, { end_pressure: ep, result: "completed" }); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || e?.message); }
  }

  return (
    <div className="space-y-5" data-testid="seq-tab-tests">
      <CapacityAnalysisCard gid={gid} fill={fill} loss={loss} />
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600">
            <Drop size={12} weight="fill" className="inline -mt-0.5 mr-1" />
            {t("seq.fill_title", "Fill tests")} ({fill.length})
          </h2>
          <button type="button" onClick={startFill} data-testid="seq-fill-start"
                  className="text-[10px] font-bold uppercase px-2 py-1.5 rounded-sm bg-blue-700 text-white inline-flex items-center gap-1">
            <Plus size={10} weight="bold" /> {t("seq.test_start", "Start test")}
          </button>
        </div>
        <TestList items={fill} kind="fill" onComplete={endFill} members={members} />
      </section>
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600">
            <FlaskIcon size={12} weight="fill" className="inline -mt-0.5 mr-1" />
            {t("seq.loss_title", "Loss tests")} ({loss.length})
          </h2>
          <button type="button" onClick={startLoss} data-testid="seq-loss-start"
                  className="text-[10px] font-bold uppercase px-2 py-1.5 rounded-sm bg-blue-700 text-white inline-flex items-center gap-1">
            <Plus size={10} weight="bold" /> {t("seq.test_start", "Start test")}
          </button>
        </div>
        <TestList items={loss} kind="loss" onComplete={endLoss} members={members} />
      </section>
    </div>
  );
}

function TestList({ items, kind, onComplete, members }) {
  const serialByDid = Object.fromEntries(
    (members || []).map(m => [m.device_id, m.serial_number || m.device_id]));
  if (items.length === 0) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-sm p-4 text-center text-slate-500 text-xs">
        No tests yet
      </div>
    );
  }
  return (
    <ul className="space-y-1.5" data-testid={`seq-test-list-${kind}`}>
      {items.map(t => (
        <li key={t.id}
            className={`bg-white border rounded-sm p-2.5 text-xs ${t.result === "pending" ? "border-amber-300" : "border-slate-200"}`}
            data-testid={`seq-test-row-${kind}-${t.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] text-slate-400">{(t.id || "").slice(0, 8)}</span>
            {t.device_id && <span className="font-mono text-slate-700 font-bold">{serialByDid[t.device_id] || t.device_id}</span>}
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm
                              ${t.result === "pending" ? "bg-amber-100 text-amber-800"
                                : t.result === "passed" || t.result === "completed" ? "bg-emerald-100 text-emerald-800"
                                : "bg-slate-100 text-slate-700"}`}>
              {t.result}
            </span>
            {t.duration_s != null && <span className="text-slate-500">{Math.round(t.duration_s)}s</span>}
            {t.start_pressure != null && <span className="text-slate-500">P {t.start_pressure} → {t.end_pressure ?? "—"} bar</span>}
            {t.leak_rate_bar_per_min != null && (
              <span className="text-slate-500">leak {t.leak_rate_bar_per_min.toFixed?.(3)} bar/min</span>
            )}
            {t.result === "pending" && (
              <button type="button" onClick={() => onComplete(t.id)}
                      data-testid={`seq-test-complete-${kind}-${t.id}`}
                      className="ml-auto text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-emerald-700 text-white">
                Complete
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}


// ════════════════════════════════════════════════════════════════════
// iter150AH — Capacity analysis card (system volume + leak rate).
//
// Pulls /capacity-analysis (which combines the latest passed fill test
// + latest completed loss test + the device's FAD@pset table) and
// renders V_system, Q_leak, leak%, plus inputs and an advisory band.
// Auto-recomputes whenever the test history changes.
// ════════════════════════════════════════════════════════════════════
function CapacityAnalysisCard({ gid, fill, loss }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const d = await seqCapacityAnalysis(gid);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed");
      setData(null);
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-line */ }, [gid, fill.length, loss.length]);

  const advisoryColor = ({
    critical: "bg-red-100 text-red-800 border-red-300",
    high:     "bg-orange-100 text-orange-800 border-orange-300",
    moderate: "bg-amber-100 text-amber-800 border-amber-300",
    ok:       "bg-emerald-100 text-emerald-800 border-emerald-300",
  })[data?.advisory] || "bg-slate-100 text-slate-700 border-slate-300";

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-3"
             data-testid="seq-capacity-analysis-card">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 inline-flex items-center gap-1.5">
          <ChartLineUp size={12} weight="bold" />
          Capacity analysis
          <span className="text-[9px] font-normal text-slate-400 normal-case tracking-normal">
            (system volume + leak rate)
          </span>
        </h2>
        <button type="button" onClick={refresh} disabled={loading}
                data-testid="seq-capacity-refresh"
                className="text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-50">
          {loading ? "…" : "Recompute"}
        </button>
      </div>
      {err && (
        <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-sm p-2"
             data-testid="seq-capacity-error">{err}</div>
      )}
      {!err && data && !data.ready && (
        <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-2"
             data-testid="seq-capacity-not-ready">
          {data.message}
          {Array.isArray(data.missing) && data.missing.length > 0 && (
            <div className="mt-1 text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Missing: {data.missing.join(", ")}
            </div>
          )}
        </div>
      )}
      {!err && data && data.ready && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <CapStat label="System volume"
                     primary={`${data.results.V_system_m3.toFixed(2)} m³`}
                     secondary={`${data.results.V_system_liters.toFixed(0)} L · ${data.results.V_system_ft3.toFixed(1)} ft³`}
                     testid="seq-capacity-vsystem" />
            <CapStat label="Leak rate"
                     primary={`${data.results.Q_leak_m3_per_min.toFixed(3)} m³/min`}
                     secondary={`${data.results.Q_leak_cfm.toFixed(1)} CFM`}
                     testid="seq-capacity-qleak" />
            <CapStat label="Leak % of FAD"
                     primary={`${data.results.leak_pct_of_fad.toFixed(1)}%`}
                     secondary={`air lost / fill: ${data.results.air_lost_fill_m3.toFixed(2)} m³`}
                     testid="seq-capacity-leakpct"
                     bandClass={advisoryColor} />
          </div>
          <div className={`text-[11px] border rounded-sm p-2 ${advisoryColor}`}
               data-testid="seq-capacity-advisory">
            <b className="uppercase tracking-wider mr-1">{data.advisory}:</b>
            {data.advisory === "critical" && "Leakage above 25% of FAD — repair leaks immediately to recover capacity."}
            {data.advisory === "high"     && "Leakage 15–25% of FAD — schedule a leak survey within the week."}
            {data.advisory === "moderate" && "Leakage 10–15% of FAD — common in older systems; plan a leak survey."}
            {data.advisory === "ok"       && "Leakage below 10% of FAD — healthy system."}
          </div>
          {/* iter150AH — Receiver-sizing recommendation tile + PDF download + manual leak-alert send. */}
          {data.receiver_recommendation?.should_recommend && (
            <div className="border border-blue-300 bg-blue-50 rounded-sm p-3"
                 data-testid="seq-capacity-receiver-rec">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-blue-900">
                    Recommended additional storage
                  </div>
                  <div className="text-xl font-bold text-blue-900 mt-0.5">
                    + {data.receiver_recommendation.next_std_size_gal} US gal
                    <span className="text-sm font-normal text-blue-700 ml-2">
                      ({Math.round(data.receiver_recommendation.next_std_size_liters)} L)
                    </span>
                  </div>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <a href={seqCapacityAnalysisPdfUrl(gid)}
                     target="_blank" rel="noopener noreferrer"
                     data-testid="seq-capacity-pdf-download"
                     className="text-[10px] font-bold uppercase px-3 py-2 rounded-sm bg-blue-700 hover:bg-blue-800 text-white inline-flex items-center gap-1.5">
                    Download PDF
                  </a>
                  <LeakAlertSendButton gid={gid} />
                </div>
              </div>
              <p className="text-[11px] text-blue-900 mt-1.5 leading-snug">
                {data.receiver_recommendation.rationale}
              </p>
            </div>
          )}
          {!data.receiver_recommendation?.should_recommend && (
            <div className="flex items-center justify-end">
              <a href={seqCapacityAnalysisPdfUrl(gid)}
                 target="_blank" rel="noopener noreferrer"
                 data-testid="seq-capacity-pdf-download"
                 className="text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1">
                Download PDF report
              </a>
            </div>
          )}
          <details className="text-[11px] text-slate-600">
            <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
              Inputs used (FAD, pressures, durations)
            </summary>
            <ul className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono pl-3">
              <li>Q_FAD: {data.inputs.Q_FAD_m3_per_min.toFixed(3)} m³/min ({data.inputs.Q_FAD_cfm.toFixed(1)} CFM)</li>
              <li>P_atm: {data.inputs.p_atm_bar_abs} bar abs</li>
              <li>t_fill: {data.inputs.t_fill_min.toFixed(2)} min</li>
              <li>t_drop: {data.inputs.t_drop_min.toFixed(2)} min</li>
              <li>dP_fill: {data.inputs.dP_fill_bar.toFixed(2)} bar</li>
              <li>dP_drop: {data.inputs.dP_drop_bar.toFixed(2)} bar</li>
            </ul>
          </details>
        </div>
      )}
    </section>
  );
}

function CapStat({ label, primary, secondary, testid, bandClass }) {
  return (
    <div className={`border rounded-sm p-2 ${bandClass || "bg-slate-50 border-slate-200"}`}
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="text-base font-bold mt-0.5">{primary}</div>
      <div className="text-[10px] opacity-70 mt-0.5">{secondary}</div>
    </div>
  );
}


// iter150AH — Manual "email this leak report now" button. Pings
// the /leak-alert-send endpoint with force=true (admin only) so the
// 24h streak / 7d cooldown can be bypassed when a customer asks for
// a fresh copy. Renders a toast + a small status chip with last send.
function LeakAlertSendButton({ gid }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    seqLeakAlertStatus(gid).then(setStatus).catch(() => null);
  }, [gid]);

  async function send() {
    if (busy) return;
    setBusy(true);
    try {
      const r = await seqLeakAlertSend(gid, /*force=*/true);
      if (r.sent) {
        const n = (r.delivered_to || []).length;
        toast.success(`Leak report emailed to ${n} recipient${n === 1 ? "" : "s"}.`);
      } else if (r.skipped) {
        toast.error(`Send skipped: ${r.skipped}`);
      } else {
        toast.message("Send completed — no recipients qualified.");
      }
      const fresh = await seqLeakAlertStatus(gid);
      setStatus(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Send failed");
    } finally { setBusy(false); }
  }

  const lastSent = status?.last_alert_at
    ? new Date(status.last_alert_at).toLocaleString()
    : null;

  return (
    <button type="button" onClick={send} disabled={busy}
            data-testid="seq-leak-alert-send"
            title={lastSent ? `Last sent: ${lastSent}` : "No previous send"}
            className="text-[10px] font-bold uppercase px-3 py-2 rounded-sm bg-amber-600 hover:bg-amber-700 text-white inline-flex items-center gap-1.5 disabled:opacity-50">
      {busy ? "Sending…" : "Email report now"}
    </button>
  );
}


// ════════════════════════════════════════════════════════════════════
// Tab 4 — Decisions (read-only)
// ════════════════════════════════════════════════════════════════════
function DecisionsTab({ gid }) {
  const t = useT();
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [eng, setEng]         = useState(null);
  const [worker, setWorker]   = useState(null);
  const [busy, setBusy]       = useState(false);

  async function refreshAll() {
    setLoading(true);
    try {
      const [d, e, w] = await Promise.all([
        seqListDecisions(gid, 100),
        seqGroupEngine(gid),
        seqEngineWorkerStatus().catch(() => null),
      ]);
      setItems(d.items || []);
      setEng(e);
      setWorker(w);
    } catch { /* surfaced via toasts on individual actions */ }
    finally { setLoading(false); }
  }
  useEffect(() => { refreshAll(); }, [gid]);

  async function toggleEngine() {
    if (busy) return;
    setBusy(true);
    try {
      if (eng?.engine_enabled) {
        await seqGroupEngineDisable(gid);
        toast.success(t("seq.engine_disabled_toast", "Engine disabled for this group."));
      } else {
        await seqGroupEngineEnable(gid);
        toast.success(t("seq.engine_enabled_toast", "Engine enabled — decisions will start appearing within 10s."));
      }
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Toggle failed");
    } finally { setBusy(false); }
  }

  async function tickNow() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await seqGroupEngineTickNow(gid);
      const n = (res?.groups?.[0]?.decisions || []).length;
      toast.success(`Tick complete (${n} decision${n === 1 ? "" : "s"})`);
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Tick failed");
    } finally { setBusy(false); }
  }

  return (
    <div data-testid="seq-tab-decisions" className="space-y-4">
      {/* Engine controls */}
      <section data-testid="seq-engine-controls"
               className="bg-white border border-slate-200 rounded-sm p-3 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Gauge size={14} className="text-blue-700" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              {t("seq.engine_title", "Sequencing engine")}
            </h2>
            <span
              data-testid={`seq-engine-status-${eng?.engine_enabled ? "on" : "off"}`}
              className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                eng?.engine_enabled
                  ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                  : "bg-slate-100 text-slate-600 border-slate-300"}`}>
              {eng?.engine_enabled ? "ON" : "OFF"}
            </span>
            {worker?.running && (
              <span className="text-[10px] text-slate-500" title="Background poller status">
                worker: {worker.tick_count} ticks · every {worker.poll_interval_s}s
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="seq-engine-tick-now"
              disabled={busy}
              onClick={tickNow}
              className="text-[11px] px-2 py-1 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40">
              Tick now
            </button>
            <button
              data-testid="seq-engine-toggle"
              disabled={busy}
              onClick={toggleEngine}
              className={`text-[11px] px-2 py-1 rounded-sm border disabled:opacity-40 ${
                eng?.engine_enabled
                  ? "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
                  : "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700"}`}>
              {eng?.engine_enabled ? "Disable" : "Enable"}
            </button>
          </div>
        </div>
        {eng?.last_decision && (
          <div data-testid="seq-engine-last-decision"
               className="text-[11px] text-slate-600 border-t border-slate-100 pt-2 mt-1">
            <span className="text-slate-400 font-mono">last:</span>{" "}
            <span className={`font-bold uppercase ${
              eng.last_decision.action === "start" ? "text-emerald-700"
                : eng.last_decision.action === "stop" ? "text-rose-700"
                : "text-slate-600"}`}>
              {eng.last_decision.action}
            </span>
            {eng.last_decision.target_device_id && (
              <span data-testid="seq-engine-last-target"
                    className="font-mono ml-1">
                → {eng.last_decision.target_serial_number || eng.last_decision.target_device_id}
              </span>
            )}
            <span className="ml-1 text-slate-500">— {eng.last_decision.reason}</span>
          </div>
        )}
        {/* iter150AG.cmd-dispatch — surface whether the worker is
            actually firing CP commands, or just logging decisions. */}
        {eng?.commands_live ? (
          <div data-testid="seq-engine-banner-live"
                className="text-[11px] leading-relaxed bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-sm px-2 py-1.5 flex items-start gap-2">
            <CheckCircle size={12} weight="fill" className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <b>Live dispatch — start/stop commands are firing to the Control Plane.</b>
              {(eng?.command_counts && Object.keys(eng.command_counts).length > 0) && (
                <span className="ml-1 text-emerald-700">
                  Last 100 decisions: {Object.entries(eng.command_counts).map(([k, v]) => `${k} ${v}`).join(" · ")}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div data-testid="seq-engine-banner-advisory"
                className="text-[11px] leading-relaxed bg-slate-50 border border-slate-300 text-slate-700 rounded-sm px-2 py-1.5 flex items-start gap-2">
            <Warning size={12} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <b>Advisory only.</b> Decisions are logged below but no commands fire to the Control Plane until the group reaches
              <code className="mx-1 font-mono text-[10px]">auto_sequencing</code>
              <span className="text-slate-500">(current: <code className="font-mono text-[10px]">{eng?.state || "idle"}</code>)</span>
              {" "}AND the engine is enabled.
            </div>
          </div>
        )}
      </section>

      <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600">
        {t("seq.decisions_title", "Sequencing decisions")}
      </h2>
      {loading ? (
        <Spinner size={16} className="mx-auto animate-spin text-slate-400" />
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-sm p-6 text-center text-slate-500 text-sm">
          {t("seq.decisions_empty", "No decisions yet — click \"Tick now\" or enable the engine to start the log.")}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {items.map(d => (
            <li key={d.id} className="bg-white border border-slate-200 rounded-sm p-2.5 text-xs"
                data-testid={`seq-decision-${d.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] text-slate-400">{(d.id || "").slice(0, 8)}</span>
                <span className="text-slate-500 font-mono text-[10px]">{d.decided_at}</span>
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm
                                  ${d.action === "start" ? "bg-emerald-100 text-emerald-800"
                                    : d.action === "stop" ? "bg-red-100 text-red-800"
                                    : "bg-slate-100 text-slate-700"}`}>
                  {d.action}
                </span>
                {d.target_device_id && (
                  <span data-testid={`seq-decision-target-${d.id}`}
                        className="font-mono text-slate-700">
                    → {d.target_serial_number || d.target_device_id}
                  </span>
                )}
                {d.reason_code && (
                  <span className="text-[10px] text-slate-400 font-mono ml-auto">{d.reason_code}</span>
                )}
              </div>
              <div className="mt-1 text-slate-600">{d.reason}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// iter150AG.cmd-dispatch — visualises whether a decision actually
// resulted in a CP command. Tooltip surfaces the CP-side command id
// (or the failure message) so operators can correlate with CP logs.
function CommandStatusPill({ issued, status, result }) {
  // `issued` is the boolean the poller sets; `status` is whatever the
  // CP returned (or "skipped" / "failed"). Coerce into 3 buckets:
  let label = "skipped";
  let cls   = "bg-slate-100 text-slate-600 border-slate-300";
  let tip   = "Engine is in advisory mode — no command sent.";
  if (issued) {
    if (status === "failed") {
      label = "cmd failed";
      cls   = "bg-rose-50 text-rose-700 border-rose-300";
      tip   = (result && result.error) || "Command failed at the Control Plane.";
    } else {
      label = `cmd ${status || "issued"}`;
      cls   = "bg-emerald-50 text-emerald-700 border-emerald-300";
      tip   = result?.id
        ? `CP command id: ${result.id}` + (result.idempotency_key
                                            ? `\nidempotency: ${result.idempotency_key}`
                                            : "")
        : "Command accepted by the Control Plane.";
    }
  }
  return (
    <span data-testid={`cmd-pill-${label.replace(/\s/g, "-")}`}
           title={tip}
           className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cls}`}>
      {label}
    </span>
  );
}


// ════════════════════════════════════════════════════════════════════
// Tab 5 — Settings (iter150AG Part 9: rotation policy + Pset tuning)
// ════════════════════════════════════════════════════════════════════
function SettingsTab({ group, onChanged }) {
  const [policy, setPolicy] = useState(group.lead_rotation_policy || "lowest_hours");
  const [thresh, setThresh] = useState(String(group.lead_rotation_threshold_hours ?? 50));
  const [pset, setPset] = useState(String(group.common_pset ?? ""));
  const [bw, setBw] = useState(String(group.bandwidth_pct ?? 5));
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await seqPatchGroup(group.id, {
        lead_rotation_policy: policy,
        lead_rotation_threshold_hours: parseInt(thresh, 10) || 50,
        common_pset: parseFloat(pset) || null,
        bandwidth_pct: parseFloat(bw) || 5.0,
      });
      onChanged?.();
    } finally { setSaving(false); }
  }

  const policies = [
    { v: "lowest_hours", label: "Lowest hours (default)",
      desc:    "Whichever compressor has accumulated the fewest run-hours starts next. "
             + "Best for new fleets where you want hours to even out organically — but a "
             + "single low-hour unit can end up doing all the cycling.",
      example: "If unit A = 1 200 h and unit B = 1 350 h, A starts every time until A overtakes B." },
    { v: "daily",        label: "Daily rotation",
      desc:    "Every calendar day the lead role swaps in a fixed order (sort-order from "
             + "the group builder). Predictable, fair wear distribution regardless of "
             + "current hours.",
      example: "Day 1 → A leads. Day 2 → B. Day 3 → A again. Works regardless of the gap in run-hours." },
    { v: "hour_delta",   label: "Hour-delta trigger",
      desc:    "Keep the natural sort-order lead UNTIL the spread (max − min hours) "
             + "crosses the threshold below. Then promote the lowest-hour unit until "
             + "the gap closes. Best for sites that prefer one stable lead but want to "
             + "catch up wear automatically.",
      example: "Threshold = 50 h. A = 1 200 h, B = 1 230 h → A keeps leading. Once B hits 1 251 h, B becomes lead until A re-passes it." },
  ];

  // iter150AG Part 15 — explanatory tooltips. Plain-English copy so
  // operators don't need to read the manual to understand the field.
  const PSET_HELP = (
    "Pressure Set-Point — the target system pressure the sequencer "
    + "tries to hold (in bar). Engine HOLDS when pressure is inside the "
    + "band, STARTS another compressor when pressure dips below "
    + "the band, STOPS when pressure climbs above it."
  );
  const BW_HELP = (
    "Bandwidth — the ±tolerance around Pset, expressed as % of Pset. "
    + "Wider band = fewer start/stop cycles (less wear) but bigger "
    + "pressure swings. Narrower band = tighter pressure but more cycling. "
    + "Typical industrial values: 3 – 8%."
  );

  return (
    <div className="space-y-5" data-testid="seq-tab-settings">
      <PolicyRecommenderCard group={group} onApplied={onChanged} />
      <section className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
          Lead rotation policy
          <LearnMoreChip slug="02-lead-rotation-policies" />
        </h3>
        <div className="space-y-2">
          {policies.map(p => (
            <label key={p.v}
                    data-testid={`settings-rotation-${p.v}`}
                    className={`flex items-start gap-2 cursor-pointer border rounded-sm p-2 ${
                      policy === p.v ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
              <input type="radio" name="rotation_policy" value={p.v}
                      checked={policy === p.v}
                      onChange={() => setPolicy(p.v)}
                      className="mt-0.5" />
              <div>
                <div className="text-xs font-bold text-slate-800">{p.label}</div>
                <div className="text-[11px] text-slate-500 leading-snug">{p.desc}</div>
                {p.example && (
                  <div className="text-[10px] text-slate-500 mt-1 italic leading-snug">
                    e.g. {p.example}
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>
        {policy === "hour_delta" && (
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
              Promotion threshold (hours)
            </span>
            <input type="number" min="1" step="1"
                    data-testid="settings-rotation-threshold"
                    value={thresh}
                    onChange={e => setThresh(e.target.value)}
                    className="mt-1 w-28 text-xs px-2 py-1.5 border border-slate-300 rounded-sm font-mono" />
            <span className="ml-2 text-[11px] text-slate-500">h</span>
          </label>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
          Pressure band
          <LearnMoreChip slug="03-pset-and-bandwidth" />
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 inline-flex items-center gap-1">
              Common Pset (bar)
              <Info size={11} className="text-slate-400 cursor-help" title={PSET_HELP} />
            </span>
            <input type="number" step="0.1" min="0.5" max="25"
                    data-testid="settings-pset"
                    value={pset}
                    onChange={e => setPset(e.target.value)}
                    className="mt-1 w-full text-xs px-2 py-1.5 border border-slate-300 rounded-sm font-mono" />
            <p className="text-[10px] text-slate-500 mt-1 leading-snug">
              Target pressure in bar. The engine holds the band around this value.
            </p>
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 inline-flex items-center gap-1">
              Bandwidth (% of Pset)
              <Info size={11} className="text-slate-400 cursor-help" title={BW_HELP} />
            </span>
            <input type="number" step="0.5" min="0.5" max="50"
                    data-testid="settings-bw"
                    value={bw}
                    onChange={e => setBw(e.target.value)}
                    className="mt-1 w-full text-xs px-2 py-1.5 border border-slate-300 rounded-sm font-mono" />
            <p className="text-[10px] text-slate-500 mt-1 leading-snug">
              Wider band = fewer start/stop cycles, larger pressure swings. Typical: 3 – 8%.
            </p>
          </label>
        </div>
      </section>

      <div className="flex">
        <button type="button"
                data-testid="settings-save"
                disabled={saving}
                onClick={save}
                className="ml-auto text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

